import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Composant FormField avec validation inline
 * Affiche une bulle d'erreur sous le champ si erreur
 */
export const FormField = ({ 
  children, 
  error, 
  label, 
  required = false,
  className = '',
  helpText = ''
}) => {
  return (
    <div className={cn("relative", className)}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={cn(
        "relative",
        error && "animate-shake"
      )}>
        {React.cloneElement(children, {
          className: cn(
            children.props.className,
            error && "border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50/50"
          )
        })}
        {error && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <AlertCircle className="h-4 w-4 text-red-500" />
          </div>
        )}
      </div>
      {error && (
        <div className="mt-1.5 flex items-start gap-1.5 animate-fadeIn">
          <div className="relative">
            <div className="absolute -top-1 left-3 w-2 h-2 bg-red-500 rotate-45"></div>
            <div className="bg-red-500 text-white text-xs px-3 py-1.5 rounded-md shadow-lg">
              {error}
            </div>
          </div>
        </div>
      )}
      {helpText && !error && (
        <p className="mt-1 text-xs text-slate-500">{helpText}</p>
      )}
    </div>
  );
};

/**
 * Hook de validation de formulaire
 */
export const useFormValidation = (initialErrors = {}) => {
  const [errors, setErrors] = React.useState(initialErrors);
  const [touched, setTouched] = React.useState({});

  const setError = (field, message) => {
    setErrors(prev => ({ ...prev, [field]: message }));
  };

  const clearError = (field) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const clearAllErrors = () => {
    setErrors({});
  };

  const setFieldTouched = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const validateField = (field, value, rules) => {
    if (rules.required && (!value || value === '')) {
      setError(field, rules.requiredMessage || 'Ce champ est obligatoire');
      return false;
    }
    if (rules.min !== undefined && Number(value) < rules.min) {
      setError(field, rules.minMessage || `La valeur minimum est ${rules.min}`);
      return false;
    }
    if (rules.max !== undefined && Number(value) > rules.max) {
      setError(field, rules.maxMessage || `La valeur maximum est ${rules.max}`);
      return false;
    }
    if (rules.pattern && !rules.pattern.test(value)) {
      setError(field, rules.patternMessage || 'Format invalide');
      return false;
    }
    if (rules.custom && !rules.custom(value)) {
      setError(field, rules.customMessage || 'Valeur invalide');
      return false;
    }
    clearError(field);
    return true;
  };

  const validateForm = (formData, validationRules) => {
    let isValid = true;
    const newErrors = {};

    Object.keys(validationRules).forEach(field => {
      const rules = validationRules[field];
      const value = formData[field];

      if (rules.required && (!value || value === '' || value === 0)) {
        newErrors[field] = rules.requiredMessage || 'Ce champ est obligatoire';
        isValid = false;
      } else if (rules.min !== undefined && Number(value) < rules.min) {
        newErrors[field] = rules.minMessage || `Minimum: ${rules.min}`;
        isValid = false;
      } else if (rules.max !== undefined && Number(value) > rules.max) {
        newErrors[field] = rules.maxMessage || `Maximum: ${rules.max}`;
        isValid = false;
      } else if (rules.pattern && value && !rules.pattern.test(value)) {
        newErrors[field] = rules.patternMessage || 'Format invalide';
        isValid = false;
      } else if (rules.custom && !rules.custom(value)) {
        newErrors[field] = rules.customMessage || 'Valeur invalide';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const getFieldError = (field) => {
    return touched[field] ? errors[field] : null;
  };

  const hasErrors = () => Object.keys(errors).length > 0;

  return {
    errors,
    touched,
    setError,
    clearError,
    clearAllErrors,
    setFieldTouched,
    validateField,
    validateForm,
    getFieldError,
    hasErrors
  };
};

/**
 * Composant d'erreur inline simple
 */
export const InlineError = ({ message, className = '' }) => {
  if (!message) return null;
  
  return (
    <div className={cn("flex items-start gap-1.5 mt-1.5 animate-fadeIn", className)}>
      <div className="relative">
        <div className="absolute -top-1 left-3 w-2 h-2 bg-red-500 rotate-45"></div>
        <div className="bg-red-500 text-white text-xs px-3 py-1.5 rounded-md shadow-lg flex items-center gap-1.5">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          {message}
        </div>
      </div>
    </div>
  );
};

export default FormField;
